"use client";

import { AnimatePresence, m } from "motion/react";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { GlitchText } from "./glitch-text";

// Context to provide transition state to child components
const ToastTransitionContext = createContext<{ isTransitioning: boolean }>({
  isTransitioning: false,
});

export const useToastTransition = () => useContext(ToastTransitionContext);

interface AnimatedToastContentProps {
  children: ReactNode;
  state?: "confirm" | "pending" | "success" | "error";
  id?: string;
}

export function AnimatedToastContent({ children, state = "confirm" }: AnimatedToastContentProps) {
  const [currentState, setCurrentState] = useState(state);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const previousStateRef = useRef(state);

  useEffect(() => {
    if (state !== previousStateRef.current) {
      // Trigger transition animation
      setIsTransitioning(true);

      // Add glitch effect during transition
      const transitionTimeout = setTimeout(() => {
        setCurrentState(state);
        setIsTransitioning(false);
        previousStateRef.current = state;
      }, 300); // Increased to match glitch duration

      return () => clearTimeout(transitionTimeout);
    }
  }, [state]);

  return (
    <ToastTransitionContext.Provider value={{ isTransitioning }}>
      <div className="relative" data-content data-transitioning={isTransitioning}>
        <AnimatePresence mode="wait">
          <m.div
            key={currentState}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {children}
          </m.div>
        </AnimatePresence>
        <div className="toast-channel-indicator">
          CH-
          {currentState === "confirm"
            ? "01"
            : currentState === "pending"
              ? "02"
              : currentState === "success"
                ? "03"
                : "04"}
        </div>
      </div>
    </ToastTransitionContext.Provider>
  );
}

// Wrapper component for text that should glitch during transitions
export function ToastText({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const { isTransitioning } = useToastTransition();
  return (
    <div className={className}>
      <GlitchText isGlitching={isTransitioning} duration={0.25}>
        {children}
      </GlitchText>
    </div>
  );
}

// Helper component for bear sprites with state-based animations
export function ToastBearSprite({
  state,
  src,
  size = 24,
}: {
  state: "confirming" | "pending" | "success" | "error";
  src: string;
  size?: number;
}) {
  return (
    <m.div
      className={`toast-bear-sprite ${state}`}
      initial={{ scale: 0.8, rotate: -10, opacity: 0 }}
      animate={{ scale: 1, rotate: 0, opacity: 1 }}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 25,
        duration: 0.4,
      }}
    >
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        className="object-contain"
        style={{
          imageRendering: "crisp-edges",
          filter: state === "error" ? "saturate(0.7) brightness(0.9)" : "none",
          width: `${size}px`,
          height: `${size}px`,
        }}
      />
    </m.div>
  );
}
